import api from "./axiosConfig";

// Mirrors IntelliWarrior.API.Models.ContractSignatureMethod (numeric enum on the wire).
export type ContractSignatureMethod = 0 | 1 | 2;
export const ContractSignatureMethods = {
  Typed: 0,
  Drawn: 1,
  Either: 2,
} as const satisfies Record<string, ContractSignatureMethod>;

// Mirrors IntelliWarrior.API.Models.ContractSignatureStatus.
export type ContractSignatureStatus = 0 | 1 | 2 | 3 | 4;
export const ContractSignatureStatuses = {
  Sent: 0,
  Opened: 1,
  Signed: 2,
  Voided: 3,
  Expired: 4,
} as const satisfies Record<string, ContractSignatureStatus>;

export interface ContractTemplateResponse {
  id: string;
  name: string;
  monthlyFeeCents: number;
  paymentDayOfMonth: number;
  servicesDescriptionOverride: string | null;
  customTerms: string | null;
  allowedSignatureMethod: ContractSignatureMethod;
  isDefault: boolean;
  isArchived: boolean;
  updatedAt: string;
}

export interface CreateContractTemplateRequest {
  name: string;
  monthlyFeeCents: number;
  paymentDayOfMonth: number;
  servicesDescriptionOverride?: string | null;
  customTerms?: string | null;
  allowedSignatureMethod: ContractSignatureMethod;
}

export type UpdateContractTemplateRequest = CreateContractTemplateRequest;

export interface LockedSectionsPreviewResponse {
  version: string;
  consumerRightsDisclosure: string;
  rightToCancel: string;
  noGuaranteedOutcomes: string;
  totalCostStructure: string;
}

export interface ContractSignatureSummaryResponse {
  id: string;
  clientId: string;
  clientName: string;
  templateId: string;
  templateName: string;
  status: ContractSignatureStatus;
  sentAt: string;
  expiresAt: string;
  openedAt: string | null;
  signedAt: string | null;
  voidedAt: string | null;
  integrityHashSha256Prefix: string | null;
}

export const contractsApi = {
  // --- Templates ---

  listTemplates: async (): Promise<ContractTemplateResponse[]> => {
    const { data } = await api.get<ContractTemplateResponse[]>("/contracts/templates");
    return data;
  },

  getTemplate: async (id: string): Promise<ContractTemplateResponse> => {
    const { data } = await api.get<ContractTemplateResponse>(`/contracts/templates/${id}`);
    return data;
  },

  createTemplate: async (
    req: CreateContractTemplateRequest
  ): Promise<ContractTemplateResponse> => {
    const { data } = await api.post<ContractTemplateResponse>("/contracts/templates", req);
    return data;
  },

  updateTemplate: async (
    id: string,
    req: UpdateContractTemplateRequest
  ): Promise<ContractTemplateResponse> => {
    const { data } = await api.put<ContractTemplateResponse>(`/contracts/templates/${id}`, req);
    return data;
  },

  setDefaultTemplate: async (id: string): Promise<void> => {
    await api.post(`/contracts/templates/${id}/set-default`);
  },

  archiveTemplate: async (id: string): Promise<void> => {
    await api.post(`/contracts/templates/${id}/archive`);
  },

  /** Returns the tenant's default template, auto-creating one on first call. */
  getOrCreateDefaultTemplate: async (): Promise<ContractTemplateResponse> => {
    const { data } = await api.get<ContractTemplateResponse>("/contracts/templates/default");
    return data;
  },

  getLockedPreview: async (): Promise<LockedSectionsPreviewResponse> => {
    const { data } = await api.get<LockedSectionsPreviewResponse>(
      "/contracts/templates/locked-preview"
    );
    return data;
  },

  // --- Signatures ---

  listContractsForClient: async (
    clientId: string
  ): Promise<ContractSignatureSummaryResponse[]> => {
    const { data } = await api.get<ContractSignatureSummaryResponse[]>(
      `/clients/${clientId}/contracts`
    );
    return data;
  },

  sendContract: async (
    clientId: string,
    templateId: string
  ): Promise<ContractSignatureSummaryResponse> => {
    const { data } = await api.post<ContractSignatureSummaryResponse>(
      `/clients/${clientId}/contracts`,
      { templateId }
    );
    return data;
  },

  voidContract: async (signatureId: string, reason?: string): Promise<void> => {
    await api.post(`/contracts/signatures/${signatureId}/void`, { reason: reason ?? null });
  },

  /** Streams the signed PDF and triggers a browser download. */
  downloadContractPdf: async (signatureId: string, fileName: string): Promise<void> => {
    const res = await api.get(`/contracts/signatures/${signatureId}/pdf`, {
      responseType: "blob",
    });
    const blob = res.data as Blob;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};
